import { useEffect, useState } from "react";
import { Body, SectionHeader } from "@sprout/typography";
import Container from "@components/ui/container";
import { Select } from "@sprout/select";
import { TextInput } from "@sprout/text_input";
import { Checkbox, Option as CheckboxOption } from "@sprout/checkbox";
import { OnSelectParam, Option } from "@sprout/select";
import BackButton from "@components/back_button.tsx";
import Button from "@/sprout/button";
import useTranslation from "@hooks/use_translation";
import useProfile from "@/queries/use_profile";
import { COUNTRIES } from "@/lib/countries";
import { US_STATES } from "@/lib/us_states";
import useCsrfToken from "@hooks/use_csrf";
import { showFlash } from "@sprout/flash.tsx";

const DoNotSell = () => {
  const { t } = useTranslation("user");
  const { data } = useProfile();
  const csrfToken = useCsrfToken();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState<Option | null>(null);
  const [state, setState] = useState<Option | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const CHECKBOX_OPTIONS = [
    {
      label: t("doNotSell.checkboxLabel"),
      value: "consent",
    },
  ];

  const findByValue = (options: Option[], value: string | null | undefined): Option | null => {
    if (!value) {
      return null;
    }

    const option = options.find((opt) => opt.value === value);
    return option || null;
  };

  useEffect(() => {
    if (data) {
      setFirstName(data.firstName || "");
      setLastName(data.lastName || "");
      setEmail(data.email || "");
      setChecked(Boolean(data.doNotSell));
      setCountry(findByValue(COUNTRIES, data.doNotSell?.country));
      setState(findByValue(US_STATES, data.doNotSell?.state));
    }
  }, [data]);

  const isUSA = country?.value === "united_states";
  const isValid =
    firstName.trim() && lastName.trim() && email.trim() && country && (!isUSA || state) && checked;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!data?.paths.doNotSellPath || !isValid) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(data.paths.doNotSellPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          authenticity_token: csrfToken,
          country: country.value,
          state: state?.value || null,
        }),
      });

      if (response.ok) {
        showFlash(t("doNotSell.success"), "success");
        window.history.back();
      } else if (response.status === 429) {
        showFlash(t("doNotSell.tooManyAttempts"), "error");
      } else {
        throw new Error("Failed to update do not sell preferences");
      }
    } catch (e) {
      showFlash(t("doNotSell.error"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const onSetCountry = (selected: OnSelectParam) => {
    setCountry(selected as Option);
    setState(null);
  };

  const onCheckboxChange = (selected: CheckboxOption | CheckboxOption[]) => {
    if (Array.isArray(selected)) {
      setChecked(selected.some((opt) => opt.value === "consent"));
    } else {
      setChecked(selected.value === "consent");
    }
  };

  return (
    <Container>
      <BackButton href="/profile" />

      <div className="flex flex-col gap-4">
        <SectionHeader as="h1">{t("doNotSell.title")}</SectionHeader>
        <Body>{t("doNotSell.subtitle")}</Body>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col gap-3">
          <div className="w-full md:w-9/12 lg:w-6/12 flex flex-col gap-3">
            <TextInput
              id="first-name"
              label={t("doNotSell.firstName")}
              value={firstName}
              onChange={(event) => setFirstName(event.currentTarget.value)}
              required
            />
            <TextInput
              id="last-name"
              label={t("doNotSell.lastName")}
              value={lastName}
              onChange={(event) => setLastName(event.currentTarget.value)}
              required
            />
            <TextInput
              id="email"
              label={t("doNotSell.email")}
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              required
            />
          </div>
          <Select
            id="country"
            label={t("doNotSell.country")}
            options={COUNTRIES}
            onSelect={onSetCountry}
            value={country}
            required
            isClearable
          />
          {isUSA && (
            <Select
              id="state"
              label={t("doNotSell.state")}
              options={US_STATES}
              onSelect={(selected) => setState(selected as Option)}
              value={state}
              required
              isClearable
            />
          )}
          <Checkbox
            id="consent"
            options={CHECKBOX_OPTIONS}
            values={checked ? [CHECKBOX_OPTIONS[0].value] : []}
            onSelect={onCheckboxChange}
            isMulti
          />
          <div className="flex justify-end gap-3">
            <Button disabled={submitting} onClick={() => window.history.back()}>
              {t("doNotSell.cancel")}
            </Button>
            <Button type="submit" primary disabled={!isValid || submitting}>
              {t("doNotSell.submit")}
            </Button>
          </div>
        </div>
      </form>
    </Container>
  );
};

export default DoNotSell;
